import React, { FC, useCallback, useEffect, useState } from 'react';
import { Resource } from '../../../types/Resource';
import { Drawer } from '@silverfort/sf-components';
import { Sorting } from '../../../types/Sorting';
import { DEFAULT_SORT_DIRECTION, DEFAULT_SORT_FIELD } from '../applications/consts';
import { ResourceFilters } from '../../../types/ResourceFilters';
import { SortDirection } from '@silverfort/sf-components/dist/Table/interface';
import { ResourcesQueryBuilder } from './ResourcesQueryBuilder';
import { ResourceDetailsDrawer } from '../resource-details/ResourceDetailsDrawer';
import { useGetResources } from '../../../api/queries/useGetResources';
import { StaleSettingsModal } from './StaleSettingsModal';
import useUpdateStaleAccessByResourceId from '../../../api/mutations/useUpdateStaleAccessByResourceId';
import { useActiveDirectoryAccessContext } from '../../../context';
import ResourcesTable from './ResourcesTable';
import { DaysRangeToValues } from '../../../types/DaysRange';

export type ResourcesViewProps = {
    applicationId?: string;
};

export const ResourcesView: FC<ResourcesViewProps> = ({ applicationId }) => {
    const { refetchSnapshot, selectedStaleResourcesDaysRange } = useActiveDirectoryAccessContext();

    const initialFilters: ResourceFilters = {
        applicationId,
        lastAccessInDays: applicationId
            ? undefined
            : DaysRangeToValues[selectedStaleResourcesDaysRange]
    };

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [filters, setFilters] = useState<ResourceFilters>(initialFilters);
    const [selectedResource, setSelectedResource] = useState<Resource | null | undefined>(null);
    const [sorting, setSorting] = useState<Sorting>({
        direction: DEFAULT_SORT_DIRECTION,
        field: DEFAULT_SORT_FIELD
    });
    const [staleSettingsModalOpen, setStaleSettingsModalOpen] = useState(false);

    const showExport = !Boolean(applicationId);

    const updateFilter = useCallback(<K extends keyof ResourceFilters>(field: K, value: ResourceFilters[K]) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    }, []);

    useEffect(() => {
        if (applicationId) {
            // when applicationId is set, clear last access filter
            updateFilter('lastAccessInDays', undefined);
        } else {
            // set last access filter according to selected stale resources days range
            updateFilter('lastAccessInDays', DaysRangeToValues[selectedStaleResourcesDaysRange]);
        }
    }, [applicationId, selectedStaleResourcesDaysRange, updateFilter]);
    
    const { fetchMoreResources, resources, refetchResources, resourcesLoading } = useGetResources(filters, sorting);

    const onSuccessUpdatingResourceStaleSettings = useCallback(() => {
        refetchResources();
        refetchSnapshot();
    }, [refetchResources, refetchSnapshot]);

    const { updateStaleAccessMutation } = useUpdateStaleAccessByResourceId(onSuccessUpdatingResourceStaleSettings);

    const handleCloseDrawer = useCallback(() => {
        setDrawerOpen(false);
        setSelectedResource(null);
    }, []);

    const handleCloseStaleSettingsModal = useCallback(() => {
        setStaleSettingsModalOpen(false);
        setSelectedResource(null);
    }, []);

    const handleOpenDrawer = useCallback((resource: Resource) => {
        setSelectedResource(resource);
        setDrawerOpen(true);
    }, []);

    const handleOpenStaleSettingsModal = useCallback((resource?: Resource) => {
        setSelectedResource(resource);
        setStaleSettingsModalOpen(true);
    }, []);

    const onSort = useCallback(
        (field: keyof Resource, direction: SortDirection) => {
            if (!resources.length) return undefined;

            setSorting({ direction, field });
        },
        [resources]
    );

    return (
        <>
            <section className="flex flex-col gap-4 h-full">
                <ResourcesQueryBuilder
                    filters={filters}
                    showExport={showExport}
                    sorting={sorting}
                    updateFilter={updateFilter}
                />

                <ResourcesTable
                    loading={resourcesLoading}
                    onResourceSelect={handleOpenDrawer}
                    onScrollEnd={fetchMoreResources}
                    onSort={onSort}
                    onUpdateResourceStaleSettings={handleOpenStaleSettingsModal}
                    resources={resources}
                />
            </section>

            <Drawer
                anchor="right"
                darkBackground
                footerActionTitle="Close"
                headerTitle={selectedResource?.name}
                onAction={handleCloseDrawer}
                onClose={handleCloseDrawer}
                open={drawerOpen}>
                {selectedResource && (
                    <div className="w-[740px] flex flex-1 flex-col overflow-hidden">
                        <ResourceDetailsDrawer resource={selectedResource} />
                    </div>
                )}
            </Drawer>

            <StaleSettingsModal
                currentState={selectedResource?.stale_after}
                onApprove={updateStaleAccessMutation}
                onClose={handleCloseStaleSettingsModal}
                open={staleSettingsModalOpen}
                resource={selectedResource}
            />
        </>
    );
};
